import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { APP_PAGE_KEYS, getSectionProps, getVisibleSections } from "../../Common/appConfig/appConfig";
import { useAppPageConfig } from "../../Common/hooks/useAppConfig";
import { useMystudioProductsV4 } from "../../Common/api/v4";
import { isPreviewDataAllowed } from "../../Common/api/v4/previewGuard";
import { MobileBottomTabBar, MobileTopHeader } from "../MobileAppShell";
import { ConfiguredPageDisabledState, ConfiguredPageIntro } from "../AppConfig/ConfiguredPageState";
import { withMobileView } from "../MobileAppShell/mobileNavigation";
import MystudioDataState from "../MystudioDataState";
import {
  isProductMockupType,
  normalizeProductType,
} from "./productImage";
import "./ProductApplyPage.css";

const FILTERS = [
  { label: "전체", value: "0" },
  { label: "사진 요청", value: "1" },
  { label: "제작중", value: "2" },
  { label: "출고완료", value: "5" },
];

const PRODUCT_TYPE_LABELS = {
  "1": "촬영",
  "2": "앨범",
  "3": "액자",
  "4": "기타상품",
  "5": "파일",
  "6": "메이크업",
  "7": "영상촬영",
};

const isKnownFilter = (value) => FILTERS.some((filter) => filter.value === value);
const normalizeFilter = (value) => (isKnownFilter(value) ? value : "1");

function formatNumber(value, fallback) {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return fallback;
  return numberValue.toLocaleString("ko-KR");
}

function buildBackPoint(filterValue) {
  return encodeURIComponent(withMobileView(`/productList?tab1=${filterValue}`));
}

const V4_PRODUCT_STATUS = {
  not_started: { filterValue: "1", label: "사진 선택 요청", action: "사진 선택하기" },
  draft: { filterValue: "1", label: "작성 중", action: "이어서 신청하기" },
  submitted: { filterValue: "2", label: "신청 접수", action: "신청 내역 보기" },
  in_production: { filterValue: "2", label: "제작 진행중", action: "진행 상태 보기" },
  completed: { filterValue: "5", label: "출고완료", action: "상세보기" },
  expired: { filterValue: "5", label: "신청 기간 종료", action: "상세보기" },
};

export function mapV4Product(item, filterValue) {
  const photoRuleConfigured = item.requestable !== false;
  const status = !photoRuleConfigured && item.status === "not_started"
    ? { filterValue: "0", label: "사진 구성 미설정", action: "계약 내용 보기" }
    : V4_PRODUCT_STATUS[item.status] || V4_PRODUCT_STATUS.not_started;
  const productType = item.type === "album" ? "2" : item.type === "frame" ? "3" : "4";
  const requestable =
    photoRuleConfigured
    && (item.status === "not_started" || item.status === "draft");
  const route = requestable
    ? `/productRequestEdit?product_id=${encodeURIComponent(item.id)}&back_point=${buildBackPoint(filterValue)}`
    : `/tabs/products?product_id=${encodeURIComponent(item.id)}&tab1=${encodeURIComponent(filterValue)}`;

  return {
    id: item.id,
    filterValue: status.filterValue,
    productType,
    badge: PRODUCT_TYPE_LABELS[productType] || "상품",
    badgeTone: productType === "3" ? "blue" : undefined,
    title: item.title,
    meta: item.optionLabel || "",
    option: item.optionLabel || "",
    state: status.label,
    date: item.dueAt ? String(item.dueAt).slice(0, 10).replace(/-/g, ".") : "",
    actionLabel: status.action,
    actionType: requestable ? "primary" : "outline",
    needsPhotoSelection: requestable,
    statusCode: item.status,
    applicationId: item.applicationId || "",
    selectedPhotoCount: item.selectedPhotoCount,
    requiredPhotoCount: item.requiredPhotoCount,
    requestable: photoRuleConfigured,
    route,
    image: item.thumbnailUrl || "",
    isFallbackImage: !item.thumbnailUrl,
    source: "erp",
  };
}

export function buildV4ProductCountData(items) {
  return {
    total_product_count: items.length,
    select_photo_count: items.filter(
      (item) => item.requestable !== false && ["not_started", "draft"].includes(item.status),
    ).length,
    edit_photo_count: items.filter((item) => item.status === "submitted").length,
    conf_photo_count: 0,
    ready_photo_count: items.filter((item) => item.status === "in_production").length,
    complete_photo_count: items.filter((item) => item.status === "completed").length,
  };
}

function ProductStatusBar() {
  return (
    <div className="product-apply-status" data-node-id="6285:27323" aria-hidden="true">
      <span className="product-apply-status-time">9:41</span>
      <span className="product-apply-status-icons">
        <span className="product-apply-signal" />
        <span className="product-apply-wifi" />
        <span className="product-apply-battery" />
      </span>
    </div>
  );
}

function ProductStatIcon({ type }) {
  if (type === "photo") {
    return (
      <svg viewBox="0 0 24 24" focusable="false">
        <path d="M5 6.25h8.5a2 2 0 0 1 2 2v1.25" />
        <path d="M4.75 17.75h9.5" />
        <path d="m7.5 14.25 2.25-2.5 2.05 2.1 1.15-1.2 1.55 1.6" />
        <path d="M5 6.25v11.5" />
        <path d="M18.25 5.75v5.5" />
        <path d="M15.5 8.5h5.5" />
      </svg>
    );
  }

  if (type === "progress") {
    return (
      <svg viewBox="0 0 24 24" focusable="false">
        <path d="M12 6.25v6l3.45 2.05" />
        <path d="M6.5 4.75 5 6.25" />
        <path d="M17.5 4.75 19 6.25" />
        <path d="M5.75 12.25a6.25 6.25 0 1 0 12.5 0 6.25 6.25 0 0 0-12.5 0Z" />
        <path d="M12 3.75v1.5" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" focusable="false">
      <path d="M8 5.75h7.75A2.25 2.25 0 0 1 18 8v9.25a2.25 2.25 0 0 1-2.25 2.25H8A2.25 2.25 0 0 1 5.75 17.25V8A2.25 2.25 0 0 1 8 5.75Z" />
      <path d="M9 4.5h5.5" />
      <path d="M8.75 10h5.5" />
      <path d="M8.75 13h4" />
      <path d="M15.75 11.5l1.1 1.1 2.4-2.7" />
    </svg>
  );
}

function StatCard({ iconType, number, unit, label, active, onClick }) {
  return (
    <button
      className={`product-apply-stat${active ? " is-active" : ""}`}
      type="button"
      aria-pressed={active}
      onClick={onClick}
    >
      <span className="product-apply-stat-icon">
        <ProductStatIcon type={iconType} />
      </span>
      <span className="product-apply-stat-value">
        <strong>{number}</strong>
        <small>{unit}</small>
      </span>
      <span className="product-apply-stat-label">{label}</span>
    </button>
  );
}

function ProductManagementSection({
  countData,
  selectedFilter,
  title = "제품 제작 관리",
  description = "계약한 상품의 사진과 문구를 입력하고 제작 진행 상태를 확인해보세요.",
  onFilterSelect,
}) {
  const totalCount = Number(countData?.total_product_count) || 0;
  const progressCount = countData
    ? (Number(countData.edit_photo_count) || 0) +
      (Number(countData.conf_photo_count) || 0) +
      (Number(countData.ready_photo_count) || 0)
    : 0;

  return (
    <section className="product-apply-management" data-node-id="6385:16513">
      <div className="product-apply-management-inner" data-node-id="6385:16517">
        <div className="product-apply-management-copy" data-node-id="6285:27770">
          <h2 data-node-id="6285:27854">{title}</h2>
          <p data-node-id="6285:27700">{description}</p>
        </div>

        <div className="product-apply-stats-grid" data-node-id="6285:27802">
          <StatCard
            iconType="contract"
            number={formatNumber(totalCount, "0")}
            unit="개"
            label="계약상품"
            active={selectedFilter === "0"}
            onClick={() => onFilterSelect("0")}
          />
          <StatCard
            iconType="photo"
            number={formatNumber(countData?.select_photo_count, "0")}
            unit="개"
            label="사진선택 필요"
            active={selectedFilter === "1"}
            onClick={() => onFilterSelect("1")}
          />
          <StatCard
            iconType="progress"
            number={formatNumber(progressCount, "15")}
            unit="개"
            label="제작 진행중"
            active={selectedFilter === "2"}
            onClick={() => onFilterSelect("2")}
          />
        </div>
      </div>
    </section>
  );
}

function FilterButton({ filter, selected, onSelect }) {
  return (
    <button
      className={`product-apply-chip${selected ? " is-active" : ""}`}
      type="button"
      aria-pressed={selected}
      onClick={() => onSelect(filter.value)}
    >
      {filter.label}
    </button>
  );
}

function ProductCard({ item, onAction }) {
  const productType = normalizeProductType(item.productType);
  const productMockup = isProductMockupType(productType);
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(item.image) && !imageFailed;
  const fallbackImage = item.source === "fallback" || item.isFallbackImage || imageFailed;

  useEffect(() => {
    setImageFailed(false);
  }, [item.image]);
  const thumbClassName = [
    "product-apply-thumb",
    productMockup ? "is-product-mockup" : "",
    fallbackImage ? "is-fallback-image" : "",
    productType === "2" ? "is-album-image" : "",
    productType === "3" ? "is-frame-image" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const handleCardKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onAction(item);
  };

  return (
    <article className="product-apply-card">
      <div
        className="product-apply-card-main"
        role="button"
        tabIndex={0}
        aria-label={`${item.title}, ${item.state}`}
        onClick={() => onAction(item)}
        onKeyDown={handleCardKeyDown}
      >
        <div className={thumbClassName}>
          {showImage ? (
            <img
              src={item.image}
              alt={`${item.title} 제품 이미지`}
              loading="lazy"
              decoding="async"
              onError={() => setImageFailed(true)}
            />
          ) : (
            <span className="product-apply-thumb__empty" role="img" aria-label="등록된 제품 이미지 없음">
              이미지 준비 중
            </span>
          )}
          <span className={`product-apply-badge${item.badgeTone ? ` is-${item.badgeTone}` : ""}`}>
            {item.badge}
          </span>
        </div>
        <div className="product-apply-info">
          <p className="product-apply-card-title">{item.title}</p>
          <p className="product-apply-meta">{item.option || item.meta}</p>
          <p className="product-apply-state-row">
            <span>{item.state}</span>
            {item.date ? <time>{item.date}</time> : null}
          </p>
        </div>
      </div>

      <button
        className={`product-apply-action is-${item.actionType || "outline"}`}
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onAction(item);
        }}
      >
        {item.actionLabel}
      </button>
    </article>
  );
}

function ProductContractsSection({
  products,
  loading = false,
  error = null,
  configured = true,
  unavailable = false,
  unavailableReason = "",
  selectedFilter,
  title = "전체 계약 상품",
  description = "",
  onFilterChange,
  onProductAction,
  onRetry,
}) {
  return (
    <section className="product-apply-contracts" data-node-id="6297:6634">
      <div className="product-apply-section-head">
        <span>
          <h2 data-node-id="6297:7672">{title}</h2>
          {description ? <p>{description}</p> : null}
        </span>
        <div className="product-apply-filters" data-node-id="6297:7737">
          {FILTERS.map((filter) => (
            <FilterButton
              filter={filter}
              key={filter.value}
              selected={filter.value === selectedFilter}
              onSelect={onFilterChange}
            />
          ))}
        </div>
      </div>

      <div className="product-apply-list" data-node-id="6368:267">
        {loading ? <MystudioDataState type="loading" compact /> : null}
        {!loading && (!configured || error) ? (
          <MystudioDataState
            type="error"
            compact
            error={error}
            title={!configured ? "제품 API가 연결되지 않았어요" : undefined}
            description={!configured ? "MyStudio API 연결 주소를 확인해 주세요." : undefined}
            onRetry={configured ? onRetry : undefined}
          />
        ) : null}
        {!loading && configured && !error && unavailable ? (
          <MystudioDataState
            type="empty"
            compact
            title="제품 정보 연동을 준비하고 있어요"
            description={unavailableReason || "스튜디오에서 제품 정보를 연동하면 이곳에 표시됩니다."}
          />
        ) : null}
        {!loading && configured && !error && !unavailable && products.length === 0 ? (
          <MystudioDataState
            type="empty"
            compact
            title="해당 상태의 계약 상품이 없어요"
            description="계약 상품이 등록되거나 상태가 변경되면 이곳에 표시됩니다."
          />
        ) : null}
        {!loading && configured && !error && !unavailable ? products.map((item, index) => (
          <ProductCard item={item} onAction={onProductAction} key={`${item.id || item.title}-${item.state}-${index}`} />
        )) : null}
      </div>
    </section>
  );
}

function ProductRequestCompleteDialog({ onConfirm }) {
  return (
    <div className="product-apply-complete-layer" role="presentation">
      <section
        className="product-apply-complete-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-apply-complete-title"
        data-node-id="6368:15777"
      >
        <div className="product-apply-complete-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M6.75 12.2 10.2 15.6 17.7 8.4" />
          </svg>
        </div>
        <h2 id="product-apply-complete-title">사진 제작 요청 완료!</h2>
        <p>사진 선택 완료 상태에서만 수정이 가능해요.</p>
        <button type="button" onClick={onConfirm}>
          확인
        </button>
      </section>
    </div>
  );
}

function ProductStatusDetailDialog({ item, onClose }) {
  const hasPhotoCount = Number.isFinite(Number(item.selectedPhotoCount))
    || Number.isFinite(Number(item.requiredPhotoCount));
  const selectedPhotoCount = Number(item.selectedPhotoCount) || 0;
  const requiredPhotoCount = Number(item.requiredPhotoCount) || 0;

  return (
    <div className="product-apply-complete-layer" role="presentation">
      <section
        className="product-apply-complete-dialog product-apply-detail-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-apply-detail-title"
      >
        <span className={`product-apply-badge${item.badgeTone ? ` is-${item.badgeTone}` : ""}`}>
          {item.badge}
        </span>
        <h2 id="product-apply-detail-title">{item.title}</h2>
        <dl className="product-apply-detail-list">
          <div>
            <dt>진행 상태</dt>
            <dd>{item.state}</dd>
          </div>
          {item.option ? (
            <div>
              <dt>상품 옵션</dt>
              <dd>{item.option}</dd>
            </div>
          ) : null}
          {item.date ? (
            <div>
              <dt>예정일</dt>
              <dd>{item.date}</dd>
            </div>
          ) : null}
          {hasPhotoCount ? (
            <div>
              <dt>선택 사진</dt>
              <dd>{selectedPhotoCount} / {requiredPhotoCount}장</dd>
            </div>
          ) : null}
          {item.applicationId ? (
            <div>
              <dt>신청 번호</dt>
              <dd>{item.applicationId}</dd>
            </div>
          ) : null}
        </dl>
        <button type="button" onClick={onClose}>
          닫기
        </button>
      </section>
    </div>
  );
}

function ProductApplyFooter({ onNavigate }) {
  return <MobileBottomTabBar activePath="/tabs/products" onNavigate={onNavigate} />;
}

export default function ProductApplyPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFilter, setSelectedFilter] = useState(() => normalizeFilter(searchParams.get("tab1")));
  const productQuery = useMystudioProductsV4();
  const { pageConfig: productPageConfig } = useAppPageConfig(APP_PAGE_KEYS.products);
  const previewDataAllowed = isPreviewDataAllowed();
  const v4ProductRows = Array.isArray(productQuery.data?.data?.items)
    ? productQuery.data.data.items
    : [];
  const productListIsLoading = productQuery.configured && productQuery.isLoading;
  const shouldShowCompleteDialog =
    previewDataAllowed
    && (searchParams.get("screen") === "request-complete" || searchParams.get("request_complete") === "1");
  const requestedProductId = searchParams.get("product_id") || "";

  useEffect(() => {
    const nextFilter = normalizeFilter(searchParams.get("tab1"));
    setSelectedFilter((currentFilter) => (currentFilter === nextFilter ? currentFilter : nextFilter));
  }, [searchParams]);

  const products = useMemo(() => {
    if (v4ProductRows.length > 0) {
      return v4ProductRows
        .filter((item) => selectedFilter === "0" || mapV4Product(item, selectedFilter).filterValue === selectedFilter)
        .map((item) => mapV4Product(item, selectedFilter));
    }

    return [];
  }, [
    v4ProductRows,
    selectedFilter,
  ]);
  const selectedProductDetail = useMemo(() => {
    if (!requestedProductId) return null;

    const product = v4ProductRows.find((item) => String(item.id) === requestedProductId);
    if (
      !product
      || (
        product.requestable !== false
        && ["not_started", "draft"].includes(product.status)
      )
    ) return null;

    return mapV4Product(product, selectedFilter);
  }, [requestedProductId, selectedFilter, v4ProductRows]);
  const resolvedProductMenuCountData = useMemo(
    () => buildV4ProductCountData(v4ProductRows),
    [v4ProductRows],
  );
  const visibleProductSections = useMemo(
    () => getVisibleSections(productPageConfig),
    [productPageConfig],
  );
  const handleFilterChange = (nextFilter) => {
    setSelectedFilter(nextFilter);
    const nextParams = new URLSearchParams(searchParams.toString());
    nextParams.set("tab1", nextFilter);
    setSearchParams(nextParams, { replace: true });
  };

  const handleBellClick = () => {
    navigate(withMobileView("/tabs/home?screen=notifications"));
  };

  const handleProductAction = (item) => {
    if (item.route) {
      navigate(withMobileView(item.route));
      return;
    }

    if (item.routeType === "request" || item.routeType === "requestFrame") {
      const requestType = item.routeType === "requestFrame" ? "&request_product_type=frame" : "";
      navigate(withMobileView(`/productRequestEdit?back_point=${buildBackPoint(selectedFilter)}${requestType}`));
      return;
    }

    window.alert("상세보기는 API로 받은 계약 상품에서 확인할 수 있습니다.");
  };

  const handleCompleteConfirm = () => {
    const nextParams = new URLSearchParams(searchParams.toString());
    nextParams.delete("screen");
    nextParams.delete("request_complete");
    setSearchParams(nextParams, { replace: true });
  };

  const handleDetailClose = () => {
    const nextParams = new URLSearchParams(searchParams.toString());
    nextParams.delete("product_id");
    setSearchParams(nextParams, { replace: true });
  };

  const renderProductSection = (section) => {
    const sectionProps = getSectionProps(section);

    if (section.key === "management") {
      return (
        <ProductManagementSection
          key={section.key}
          {...sectionProps}
          countData={resolvedProductMenuCountData}
          selectedFilter={selectedFilter}
          onFilterSelect={handleFilterChange}
        />
      );
    }

    if (section.key === "product_list") {
      return (
        <ProductContractsSection
          key={section.key}
          {...sectionProps}
          products={products}
          loading={productListIsLoading}
          configured={productQuery.configured}
          error={productQuery.error}
          unavailable={productQuery.data?.data?.availability === "unavailable"}
          unavailableReason={productQuery.data?.data?.reasonCode || ""}
          selectedFilter={selectedFilter}
          onFilterChange={handleFilterChange}
          onProductAction={handleProductAction}
          onRetry={productQuery.refetch}
        />
      );
    }

    return null;
  };

  if (productPageConfig.page.enabled === false) {
    return (
      <ConfiguredPageDisabledState
        pageConfig={productPageConfig}
        activePath="/tabs/products"
        onNavigate={navigate}
      />
    );
  }

  return (
    <section className="product-apply-page mobile-app-frame" data-node-id="6285:27322">
      <ProductStatusBar />
      <MobileTopHeader onBellClick={handleBellClick} />

      <main className="product-apply-scroll" data-node-id="6385:16514">
        <ConfiguredPageIntro pageConfig={productPageConfig} />
        {visibleProductSections.map(renderProductSection)}
      </main>

      <ProductApplyFooter onNavigate={navigate} />
      {selectedProductDetail ? (
        <ProductStatusDetailDialog item={selectedProductDetail} onClose={handleDetailClose} />
      ) : null}
      {shouldShowCompleteDialog ? <ProductRequestCompleteDialog onConfirm={handleCompleteConfirm} /> : null}
    </section>
  );
}
